import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  LinearProgress,
} from "@mui/material";
import { BulkGenerationStatusDto } from "@swagger/typescript-fetch-goflyer";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

interface AutomatedLayoutTestModalProps {
  open: boolean;
  onClose: () => void;
  data: BulkGenerationStatusDto | null;
}

const formatTime = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
};

export const AutomatedLayoutTestModal: React.FC<
  AutomatedLayoutTestModalProps
> = ({ open, onClose, data }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="figma-template-automated-layout-test-dialog"
    >
      <DialogTitle>{t(messages.Automated_Layout_Test_Status())}</DialogTitle>
      <DialogContent>
        {data ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2">
                {t(messages.Found())}:
              </Typography>
              <Typography variant="body2">
                {data.found ? t(messages.Yes()) : t(messages.No())}
              </Typography>
            </Box>
            {data.jobId && (
              <Box>
                <Typography variant="subtitle2">
                  {t(messages.Job_ID())}:
                </Typography>
                <Typography variant="body2">{data.jobId}</Typography>
              </Box>
            )}
            {data.state && (
              <Box>
                <Typography variant="subtitle2">
                  {t(messages.State())}:
                </Typography>
                <Chip
                  label={data.state}
                  color={
                    data.state === BulkGenerationStatusDto.StateEnum.Completed
                      ? "success"
                      : data.state === BulkGenerationStatusDto.StateEnum.Failed
                      ? "error"
                      : "default"
                  }
                  size="small"
                />
              </Box>
            )}
            {data.progress !== undefined && (
              <Box>
                <Typography variant="subtitle2">
                  {t(messages.Progress())}:
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={data.progress}
                  sx={{
                    mt: 1,
                    mb: 0.5,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#f5822020",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#f58220",
                    },
                  }}
                />
                <Typography variant="caption">{data.progress}%</Typography>
              </Box>
            )}
            {data.estimatedTotalJobs !== undefined && (
              <Box>
                <Typography variant="subtitle2">
                  {t(messages.Estimated_Total_Jobs())}:
                </Typography>
                <Typography variant="body2">
                  {data.estimatedTotalJobs}
                </Typography>
              </Box>
            )}
            {data.estimatedTotalTimeSeconds !== undefined && (
              <Box>
                <Typography variant="subtitle2">
                  {t(messages.Estimated_Total_Time())}:
                </Typography>
                <Typography variant="body2">
                  {formatTime(data.estimatedTotalTimeSeconds)}
                </Typography>
              </Box>
            )}
            {data.estimatedRemainingTimeSeconds !== undefined && (
              <Box>
                <Typography variant="subtitle2">
                  {t(messages.Estimated_Remaining_Time())}:
                </Typography>
                <Typography variant="body2">
                  {formatTime(data.estimatedRemainingTimeSeconds)}
                </Typography>
              </Box>
            )}
            {(data.estimatedTotalTimeSeconds !== undefined ||
              data.estimatedRemainingTimeSeconds !== undefined) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                {t(messages.Estimated_Times_Note())}
              </Typography>
            )}
            {data.failedReason && (
              <Box>
                <Typography variant="subtitle2" color="error">
                  {t(messages.Failed_Reason())}:
                </Typography>
                <Typography variant="body2" color="error">
                  {typeof data.failedReason === "object"
                    ? JSON.stringify(data.failedReason)
                    : data.failedReason}
                </Typography>
              </Box>
            )}
          </Stack>
        ) : (
          <Typography>{t(messages.No_status_data_available())}.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        {data?.flyerItemList?.id && (
          <Button
            onClick={() => {
              window.open(`/createflyer/${data.flyerItemList?.id}`, "_blank");
            }}
            sx={{
              color: "#fff",
              backgroundColor: "#f58220",
              "&:hover": {
                backgroundColor: "#d96f1a",
              },
            }}
            data-testid="figma-template-view-flyer-from-status-button"
          >
            {t(messages.ViewFlyer())}
          </Button>
        )}
        <Button
          onClick={onClose}
          sx={{
            color: "#fff",
            backgroundColor: "#f44336",
            "&:hover": {
              backgroundColor: "#d32f2f",
            },
          }}
        >
          {t(messages.Close())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
